/**
 * Rule Switch content: types, level params, attribute pools, seeded objectSequence + ruleSchedule.
 * Spec: objects { id, shape, color, size, pattern }; rule changes mid-round.
 */

export type RuleAttribute = "color" | "shape" | "size" | "pattern";

export interface RuleSwitchObject {
  id: string;
  shape: string;
  color: string;
  size: string;
  pattern: string;
}

export interface RuleScheduleEntry {
  rule: RuleAttribute;
  fromIndex: number;
}

export interface RuleSwitchLevelParams {
  objectCount: number;
  binCount: number;
  switchCount: number;
  /** Display time per object in ms (for reference; objects stay until dragged) */
  objectDisplayMs: number;
}

export interface RoundContent {
  objectSequence: RuleSwitchObject[];
  ruleSchedule: RuleScheduleEntry[];
  levelParams: RuleSwitchLevelParams;
}

/** Attribute pools matching spec and content-bank.json ruleSwitch */
export const COLORS = ["red", "blue", "green", "yellow", "purple", "orange"] as const;
export const SHAPES = ["circle", "square", "triangle", "star", "diamond", "hexagon"] as const;
export const SIZES = ["small", "large"] as const;
export const PATTERNS = ["solid", "striped", "dotted"] as const;

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function pick<T>(arr: readonly T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)];
}

export function getLevelParams(level: number): RuleSwitchLevelParams {
  const clamped = Math.max(1, Math.min(10, level));
  if (clamped <= 2) {
    return { objectCount: 8, binCount: 2, switchCount: 0, objectDisplayMs: 4000 };
  }
  if (clamped <= 4) {
    return { objectCount: 10, binCount: 2, switchCount: 1, objectDisplayMs: 3500 };
  }
  if (clamped <= 6) {
    return { objectCount: 12, binCount: 3, switchCount: 2, objectDisplayMs: 3000 };
  }
  if (clamped <= 8) {
    return { objectCount: 15, binCount: 3, switchCount: 3, objectDisplayMs: 3000 };
  }
  return { objectCount: 18, binCount: 3, switchCount: 3, objectDisplayMs: 2000 };
}

function shuffleWithSeed<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Build deterministic object sequence from seed and level params */
export function buildObjectSequence(
  seed: string,
  levelParams: RuleSwitchLevelParams
): RuleSwitchObject[] {
  const rng = seededRandom(seed);
  const seq: RuleSwitchObject[] = [];
  for (let i = 0; i < levelParams.objectCount; i++) {
    seq.push({
      id: `obj-${i}-${seed.slice(0, 4)}`,
      shape: pick(SHAPES, rng),
      color: pick(COLORS, rng),
      size: pick(SIZES, rng),
      pattern: pick(PATTERNS, rng),
    });
  }
  return seq;
}

/** Build rule schedule: first rule from 0, then switch indices. */
export function buildRuleSchedule(
  seed: string,
  levelParams: RuleSwitchLevelParams
): RuleScheduleEntry[] {
  const rules: RuleAttribute[] = ["color", "shape", "size", "pattern"];
  const rng = seededRandom(seed);
  const firstRule = rules[Math.floor(rng() * rules.length)];
  const schedule: RuleScheduleEntry[] = [{ rule: firstRule, fromIndex: 0 }];
  if (levelParams.switchCount === 0) return schedule;

  const n = levelParams.objectCount;
  const switchIndices: number[] = [];
  const minGap = 4;
  const maxFirstSwitch = Math.max(minGap, n - (levelParams.switchCount * minGap));
  let prev = Math.floor(minGap + rng() * (maxFirstSwitch - minGap));
  switchIndices.push(prev);
  for (let s = 1; s < levelParams.switchCount; s++) {
    const nextMin = prev + minGap;
    if (nextMin >= n - 1) break;
    const nextMax = n - (levelParams.switchCount - s) * minGap;
    prev = Math.floor(nextMin + rng() * (Math.min(nextMax, n - 1) - nextMin));
    switchIndices.push(prev);
  }

  const otherRules = rules.filter((r) => r !== firstRule);
  const shuffledOthers = shuffleWithSeed(otherRules, rng);
  for (let i = 0; i < switchIndices.length; i++) {
    schedule.push({
      rule: shuffledOthers[i % shuffledOthers.length],
      fromIndex: switchIndices[i],
    });
  }
  schedule.sort((a, b) => a.fromIndex - b.fromIndex);
  return schedule;
}

/**
 * Resolve round content: build objectSequence and ruleSchedule from seed.
 * Optionally merge backend items as schedule override if present (future).
 */
export function resolveRoundContent(
  contentSeed: string,
  roundIndex: number,
  levelParams: RuleSwitchLevelParams,
  _backendData?: unknown,
  _sessionSeed?: string
): RoundContent {
  const effectiveSeed = contentSeed
    ? `${contentSeed}-${roundIndex}`
    : `local-${roundIndex}-${Date.now()}`;
  const objectSequence = buildObjectSequence(effectiveSeed, levelParams);
  const ruleSchedule = buildRuleSchedule(effectiveSeed, levelParams);
  return {
    objectSequence,
    ruleSchedule,
    levelParams,
  };
}

/** Get the active rule at a given object index */
export function getRuleAtIndex(
  ruleSchedule: RuleScheduleEntry[],
  objectIndex: number
): RuleAttribute {
  let rule = ruleSchedule[0].rule;
  for (let i = 1; i < ruleSchedule.length; i++) {
    if (objectIndex >= ruleSchedule[i].fromIndex) {
      rule = ruleSchedule[i].rule;
    }
  }
  return rule;
}

/** Get bin labels for current rule: partition attribute pool into binCount groups; returns label per bin (e.g. "Red, Blue, Green"). */
export function getBinLabelsForRule(
  rule: RuleAttribute,
  binCount: number
): string[] {
  const pool =
    rule === "color"
      ? [...COLORS]
      : rule === "shape"
        ? [...SHAPES]
        : rule === "size"
          ? [...SIZES]
          : [...PATTERNS];
  const n = pool.length;
  const perBin = Math.ceil(n / binCount);
  const labels: string[] = [];
  for (let b = 0; b < binCount; b++) {
    const start = b * perBin;
    const chunk = pool.slice(start, Math.min(start + perBin, n));
    labels.push(chunk.join(", "));
  }
  return labels;
}

/** Which bin index (0-based) does this object belong to for the given rule? Uses same partition as getBinLabelsForRule. */
export function getCorrectBinIndex(
  obj: RuleSwitchObject,
  rule: RuleAttribute,
  binCount: number
): number {
  const pool =
    rule === "color"
      ? [...COLORS]
      : rule === "shape"
        ? [...SHAPES]
        : rule === "size"
          ? [...SIZES]
          : [...PATTERNS];
  const value = obj[rule] as string;
  const idx = (pool as readonly string[]).indexOf(value);
  if (idx < 0) return 0;
  const n = pool.length;
  const perBin = Math.ceil(n / binCount);
  return Math.min(Math.floor(idx / perBin), binCount - 1);
}
